import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react";
import { deepResearchStepPopupState } from "./DeepResearchStepPopupState";
import { makeApi, URLS } from "Utils/apiURL";
import deepResearchStore from "Stores/deepResearchData";
import SellerProfileSetup from "Pages/Dashboard/DeepResearch/components/SellerProfileSetup";

const labelForProfile = (p) =>
  p?.name || p?.company_name || p?.product_name || "Unnamed profile";

const OBJECTIVE_CHIPS = [
  "Get a reply",
  "Book a 15-min call",
  "Drive to website",
  "Demo signup",
  "Event invite"
];

const DeepResearchStepPopup = observer(() => {
  const { showPopup, popupValues } = deepResearchStepPopupState;
  const { onSave, currentStep, daysFromStart, isEdit, existingConfig } = popupValues || {};

  const [sellerProfileId, setSellerProfileId] = useState(existingConfig?.sellerProfileId || "");
  const [objectiveText, setObjectiveText] = useState(existingConfig?.objectiveText || "");
  const [requireApproval, setRequireApproval] = useState(
    existingConfig?.requireApproval !== undefined ? existingConfig.requireApproval : true
  );
  const [freshnessDays, setFreshnessDays] = useState(existingConfig?.freshnessDays || 30);
  const [showSellerSetup, setShowSellerSetup] = useState(false);
  const [editSellerProfile, setEditSellerProfile] = useState(null);
  const [sellerMenuOpen, setSellerMenuOpen] = useState(false);
  const sellerMenuRef = useRef(null);

  // Close seller dropdown on outside click
  useEffect(() => {
    if (!sellerMenuOpen) return;
    const onDoc = (e) => {
      if (sellerMenuRef.current && !sellerMenuRef.current.contains(e.target)) {
        setSellerMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sellerMenuOpen]);

  // Hydrate seller profiles when popup opens
  useEffect(() => {
    if (showPopup) {
      deepResearchStore.fetchSellerProfiles({ force: true }).then(() => {
        // Auto-select first profile if none chosen yet and one exists
        const profiles = deepResearchStore.sellerProfiles || [];
        if (!sellerProfileId && profiles.length > 0) {
          setSellerProfileId(profiles[0].id);
        }
      });
      // Refresh state from existingConfig in case it changed
      if (existingConfig) {
        setSellerProfileId(existingConfig.sellerProfileId || "");
        setObjectiveText(existingConfig.objectiveText || "");
        setRequireApproval(existingConfig.requireApproval !== undefined ? existingConfig.requireApproval : true);
        setFreshnessDays(existingConfig.freshnessDays || 30);
      }
    }
  }, [showPopup]);

  if (!showPopup) return null;

  const profiles = deepResearchStore.sellerProfiles || [];

  const handleClose = () => {
    deepResearchStepPopupState.reset();
  };

  const handleSave = () => {
    if (!sellerProfileId) {
      alert("Please select a seller profile");
      return;
    }
    if (!objectiveText || !objectiveText.trim()) {
      alert("Please enter an outreach objective");
      return;
    }

    const config = {
      sellerProfileId,
      objectiveText: objectiveText.trim(),
      requireApproval,
      freshnessDays: parseInt(freshnessDays, 10) || 30
    };

    if (typeof onSave === "function") {
      onSave({
        stageType: "deepResearch",
        currentStep,
        daysFromStart,
        config
      });
    }
    handleClose();
  };

  return (
    <div className="drStepPopupOverlay" onClick={handleClose}>
      <div className="drStepPopupCard" onClick={e => e.stopPropagation()}>
        <div className="drStepPopupHeader">
          <h3>{isEdit ? "Edit" : "Add"} Deep Research step</h3>
          <span className="drStepPopupClose" onClick={handleClose}>✕</span>
        </div>

        <div className="drStepPopupBody">
          <div className="drStepPopupField">
            <label>Seller</label>
            {deepResearchStore.sellerProfilesLoading && profiles.length === 0 ? (
              <div className="drStepPopupSellerLoading">
                <span className="drStepPopupSellerSpinner" />
                <span>Loading sellers…</span>
              </div>
            ) : profiles.length > 0 ? (
              <div className="drStepPopupSellerRow">
                <div className="drSellerDropdown" ref={sellerMenuRef} style={{ flex: 1 }}>
                  <button
                    type="button"
                    className={`drSellerDropdownTrigger ${sellerMenuOpen ? "active" : ""}`}
                    onClick={() => setSellerMenuOpen(o => !o)}
                  >
                    <span className="drSellerDropdownText">
                      {sellerProfileId
                        ? labelForProfile(profiles.find(p => p.id === sellerProfileId))
                        : "No seller profile"}
                    </span>
                    <i className={`material-icons drSellerDropdownArrow ${sellerMenuOpen ? "open" : ""}`}>
                      expand_more
                    </i>
                  </button>

                  {sellerMenuOpen && (
                    <div className="drSellerDropdownMenu">
                      {profiles.map(p => {
                        const checked = p.id === sellerProfileId;
                        return (
                          <label
                            key={p.id}
                            className={`drSellerDropdownItem ${checked ? "checked" : ""}`}
                          >
                            <input
                              type="radio"
                              name="drStepSellerProfile"
                              checked={checked}
                              onChange={() => {
                                setSellerProfileId(p.id);
                                setSellerMenuOpen(false);
                              }}
                            />
                            <span className="drSellerRadio" />
                            <span className="drSellerItemLabel">{labelForProfile(p)}</span>
                          </label>
                        );
                      })}
                      <button
                        type="button"
                        className="drSellerDropdownAddNew"
                        onClick={() => {
                          setSellerMenuOpen(false);
                          setEditSellerProfile(null);
                          setShowSellerSetup(true);
                        }}
                      >
                        <i className="material-icons">add</i>
                        <span>Add new seller</span>
                      </button>
                      <a
                        className="drSellerDropdownManage"
                        href="/settings/seller-profiles"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setSellerMenuOpen(false)}
                      >
                        <i className="material-icons">open_in_new</i>
                        <span>Manage all profiles</span>
                      </a>
                    </div>
                  )}
                </div>

                {sellerProfileId && (
                  <button
                    type="button"
                    className="drSellerEditBtn"
                    onClick={() => {
                      const current = profiles.find(p => p.id === sellerProfileId) || null;
                      setEditSellerProfile(current);
                      setShowSellerSetup(true);
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            ) : (
              <div className="drStepPopupHint" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span>No seller profiles yet.</span>
                <button
                  type="button"
                  className="drStepPopupBtn primary"
                  onClick={() => {
                    setEditSellerProfile(null);
                    setShowSellerSetup(true);
                  }}
                >
                  + Create seller profile
                </button>
              </div>
            )}
          </div>

          <div className="drStepPopupField">
            <label>Outreach goal</label>
            <div className="drStepPopupChips">
              {OBJECTIVE_CHIPS.map(chip => (
                <button
                  key={chip}
                  className="drStepPopupChip"
                  onClick={() => setObjectiveText(chip)}
                  type="button"
                >
                  {chip}
                </button>
              ))}
            </div>
            <textarea
              rows={3}
              value={objectiveText}
              onChange={e => setObjectiveText(e.target.value)}
              placeholder="e.g. Get a 15-min call to discuss enterprise tier"
            />
          </div>

          <div className="drStepPopupField">
            <label>Reuse existing research if newer than (days)</label>
            <input
              type="number"
              min="0"
              max="365"
              value={freshnessDays}
              onChange={e => setFreshnessDays(e.target.value)}
            />
          </div>

          <div className="drStepPopupField drStepPopupCheckbox">
            <label>
              <input
                type="checkbox"
                checked={requireApproval}
                onChange={e => setRequireApproval(e.target.checked)}
              />
              <span>Approve content before sending</span>
            </label>
            <div className="drStepPopupHint">
              When on, you'll review each contact's personalized messages before they're scheduled.
              When off, content is auto-scheduled (you can still edit before send).
            </div>
          </div>
        </div>

        <div className="drStepPopupFooter">
          <button className="drStepPopupBtn secondary" onClick={handleClose}>Cancel</button>
          <button className="drStepPopupBtn primary" onClick={handleSave}>Save step</button>
        </div>
      </div>

      {showSellerSetup && (
        <SellerProfileSetup
          existingProfile={editSellerProfile}
          onClose={() => setShowSellerSetup(false)}
          onSave={(payload) => {
            const editingId = editSellerProfile?.id || deepResearchStore.activeSellerProfileId;
            const body = editingId ? { ...payload, id: editingId } : payload;
            deepResearchStore.saveSellerProfile(body).then(saved => {
              if (saved && saved.id) {
                setSellerProfileId(saved.id);
              }
              setShowSellerSetup(false);
            });
          }}
        />
      )}
    </div>
  );
});

export default DeepResearchStepPopup;
